import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
    fetchPricingPlans,
    fetchPricingSummary,
    changePlan,
    clearPricingError,
    clearPricingMessage,
    selectPricingPlans,
    selectSelectedPlan,
    selectPricingLoading,
    selectPricingError,
    selectPricingMessage,
} from '../../store/slices/system/pricingSlice';
import {
    ClipboardList,
    AlertTriangle,
    Check,
    CheckCircle2,
    Loader2,
    Crown,
    CreditCard,
    Star,
    LayoutGrid,
    List,
    Search,
    Tag,
    Zap,
    Users,
} from 'lucide-react';

const Pricing = ({ showToast }) => {
    const dispatch = useDispatch();
    const { openModal, closeModal } = useModal();

    // ==================== STATE ====================
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);

    // ==================== REFS ====================
    const lastToastRef = useRef({ message: null, timestamp: 0 });

    // ==================== SELECTORS ====================
    const plans = useSelector(selectPricingPlans) || [];
    const selectedPlan = useSelector(selectSelectedPlan);
    const loading = useSelector(selectPricingLoading);
    const error = useSelector(selectPricingError);
    const message = useSelector(selectPricingMessage);

    // ==================== EFFECTS ====================
    useEffect(() => {
        dispatch(fetchPricingPlans());
        dispatch(fetchPricingSummary());
        return () => {
            dispatch(clearPricingError());
            dispatch(clearPricingMessage());
        };
    }, [dispatch]);

    // ==================== TOAST HANDLING ====================
    const showToastMessage = useCallback((msg, type = 'info') => {
        if (showToast && typeof showToast === 'function') {
            showToast(msg, type);
        }
    }, [showToast]);

    useEffect(() => {
        const msg = message || (error ? error : null);
        if (!msg || !showToast || !shouldShowApiToast(msg, !!error)) return;

        const messageKey = `${msg}_${error ? 'error' : 'success'}`;
        const now = Date.now();

        if (lastToastRef.current.message === messageKey &&
            (now - lastToastRef.current.timestamp) < 2000) {
            return;
        }

        lastToastRef.current = { message: messageKey, timestamp: now };

        const toastType = error ? 'error' : 'success';
        showToast(msg, toastType);

        setTimeout(() => {
            dispatch(clearPricingMessage());
            dispatch(clearPricingError());
        }, 500);
    }, [message, error, dispatch, showToast]);

    // ==================== FILTERING & PAGINATION ====================
    const filteredPlans = plans.filter(plan => {
        const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             plan.features?.some(f => f.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesType = filterType === '' ||
                           (filterType === 'custom' && plan.is_custom) ||
                           (filterType === 'standard' && !plan.is_custom);
        return matchesSearch && matchesType;
    });

    const totalFiltered = filteredPlans.length;
    const totalPages = Math.ceil(totalFiltered / itemsPerPage);
    const paginatedPlans = filteredPlans.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterType]);

    // ==================== STATS ====================
    const totalPlans = plans.length;
    const currentPlanName = selectedPlan?.name || 'None';
    const mostPopular = plans.find(p => p.is_featured);
    const customPlans = plans.filter(p => p.is_custom).length;

    // ==================== HANDLERS ====================
    const handleSelectPlan = (plan) => {
        if (plan.id === selectedPlan?.id) {
            showToastMessage(`✅ Already on the ${plan.name} plan`, 'info');
            return;
        }

        openModal(
            <div>
                <div className="modal-header">
                    <div className="modal-title">
                        <ClipboardList size={16} style={{ verticalAlign: '-3px' }} />
                        Switch to {plan.name} Plan
                    </div>
                    <button className="modal-close" onClick={closeModal}>×</button>
                </div>
                <div className="modal-body">
                    <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '12px' }}>
                        You are about to switch from <strong>{selectedPlan?.name || 'Free'}</strong> to <strong>{plan.name}</strong>.
                    </p>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        marginBottom: '16px'
                    }}>
                        <div style={{
                            padding: '12px',
                            background: 'var(--cream)',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>Current Plan</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
                                {selectedPlan?.name || 'Free'}
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
                                {selectedPlan?.is_custom ? 'Custom' : `${selectedPlan?.currency || '$'}${selectedPlan?.price || 0}`}/mo
                            </div>
                        </div>
                        <div style={{
                            padding: '12px',
                            background: 'var(--gold-pale)',
                            borderRadius: '8px',
                            textAlign: 'center',
                            border: '2px solid var(--gold)'
                        }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>New Plan</div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--gold)' }}>{plan.name}</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
                                {plan.is_custom ? 'Custom' : `${plan.currency}${plan.price}`}{plan.period || ''}
                            </div>
                        </div>
                    </div>
                    <div style={{
                        padding: '12px',
                        background: 'var(--amber-bg)',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: 'var(--amber)'
                    }}>
                        <AlertTriangle size={16} style={{ verticalAlign: '-3px' }} />
                        Your plan will be prorated and the new rate will apply immediately.
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
                    <button
                        className="btn btn-gold btn-sm"
                        disabled={loading}
                        onClick={async () => {
                            try {
                                await dispatch(changePlan(plan.id)).unwrap();
                                showToastMessage(`✅ Switched to ${plan.name} plan!`, 'success');
                                // Refetch summary to update the selected plan
                                await dispatch(fetchPricingSummary());
                                closeModal();
                            } catch (err) {
                                showToastMessage(err.message || 'Failed to switch plan', 'error');
                            }
                        }}
                    >
                        {loading ? 'Processing...' : 'Confirm Switch'}
                    </button>
                </div>
            </div>
        );
    };

    // ==================== RENDER HELPERS ====================
    const getPlanIcon = (plan) => {
        if (plan.is_featured) return <Zap size={18} />;
        if (plan.is_custom) return <Users size={18} />;
        return <Tag size={18} />;
    };

    const getPriceDisplay = (plan) => {
        if (plan.is_custom) return 'Custom';
        return `${plan.currency}${plan.price}`;
    };

    const getPeriodDisplay = (plan) => {
        if (plan.is_custom) return '';
        return plan.period || '/mo';
    };

    const getSmsFeature = (plan) => {
        const sms = plan.features?.find(f => f.toLowerCase().includes('sms'));
        return sms || (plan.is_custom ? 'Unlimited SMS' : 'SMS credits included');
    };

    // ==================== GRID VIEW ====================
    const renderGridView = () => (
        <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {paginatedPlans.map(plan => {
                const isCurrent = plan.id === selectedPlan?.id;
                const color = isCurrent ? 'var(--gold)' :
                              plan.is_featured ? 'var(--blue)' :
                              plan.is_custom ? 'var(--purple)' : 'var(--navy)';

                return (
                    <div key={plan.id} className="card" style={{ borderTop: `3px solid ${color}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                            <div style={{
                                width: '46px',
                                height: '46px',
                                borderRadius: '12px',
                                background: `${color}18`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '22px',
                                flexShrink: 0,
                                color: color,
                            }}>
                                {getPlanIcon(plan)}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{plan.name}</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{plan.description || 'No description'}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                {isCurrent && (
                                    <span className="badge badge-gold" style={{ background: 'var(--gold)', color: 'var(--navy)', fontSize: '10px' }}>
                                        Current
                                    </span>
                                )}
                                {plan.is_featured && !isCurrent && (
                                    <span className="badge badge-blue" style={{ background: 'var(--blue)', color: 'white', fontSize: '10px' }}>
                                        <Crown size={10} style={{ verticalAlign: '-1px' }} /> Most Popular
                                    </span>
                                )}
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '8px',
                            marginBottom: '8px',
                        }}>
                            <span style={{
                                fontFamily: 'Playfair Display, serif',
                                fontSize: '26px',
                                fontWeight: '700',
                                color: 'var(--navy)',
                            }}>
                                {getPriceDisplay(plan)}
                            </span>
                            <span style={{
                                fontSize: '14px',
                                color: 'var(--text-light)',
                                fontWeight: '400',
                            }}>
                                {getPeriodDisplay(plan)}
                            </span>
                            <span style={{
                                marginLeft: 'auto',
                                fontSize: '12px',
                                color: 'var(--text-light)',
                            }}>
                                {getSmsFeature(plan)}
                            </span>
                        </div>

                        <div style={{
                            margin: '10px 0',
                            borderTop: '1px solid var(--border)',
                            paddingTop: '10px',
                            maxHeight: '150px',
                            overflowY: 'auto',
                        }}>
                            {plan.features?.map((feature, idx) => (
                                <div key={idx} style={{
                                    fontSize: '12px',
                                    color: 'var(--text-mid)',
                                    padding: '2px 0',
                                }}>
                                    <Check size={12} style={{ verticalAlign: '-2px' }} /> {feature}
                                </div>
                            ))}
                        </div>

                        <button
                            className={`btn ${isCurrent ? 'btn-ghost' : 'btn-gold'} btn-sm`}
                            style={{ width: '100%', marginTop: '4px' }}
                            onClick={() => handleSelectPlan(plan)}
                            disabled={isCurrent || loading}
                        >
                            {isCurrent ? (
                                <><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Current Plan</>
                            ) : (
                                plan.cta_text || 'Select Plan'
                            )}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    // ==================== LIST VIEW ====================
    const renderListView = () => (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Pricing Plans</div>
            </div>
            <div className="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Plan</th>
                            <th>Price</th>
                            <th>SMS</th>
                            <th>Features</th>
                            <th>Status</th>
                            <th style={{ minWidth: '120px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedPlans.map(plan => {
                            const isCurrent = plan.id === selectedPlan?.id;
                            return (
                                <tr key={plan.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '8px',
                                                background: isCurrent ? 'var(--gold)' : 'var(--blue)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                            }}>
                                                {getPlanIcon(plan)}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{plan.name}</div>
                                                {plan.is_featured && <span className="badge badge-blue" style={{ background: 'var(--blue)', color: 'white', fontSize: '9px' }}>Popular</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: '600' }}>
                                        {getPriceDisplay(plan)}<span style={{ fontSize: '12px', color: 'var(--text-light)' }}>{getPeriodDisplay(plan)}</span>
                                    </td>
                                    <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{getSmsFeature(plan)}</td>
                                    <td style={{ fontSize: '12px', color: 'var(--text-mid)' }}>
                                        {plan.features?.join(', ')}
                                    </td>
                                    <td>
                                        {isCurrent ? (
                                            <span className="badge badge-gold" style={{ background: 'var(--gold)', color: 'var(--navy)' }}>Current</span>
                                        ) : (
                                            <span className="badge badge-navy">Available</span>
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            className={`btn ${isCurrent ? 'btn-ghost' : 'btn-gold'} btn-sm`}
                                            onClick={() => handleSelectPlan(plan)}
                                            disabled={isCurrent || loading}
                                        >
                                            {isCurrent ? 'Current' : 'Select'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px',
                    padding: '14px 20px',
                    borderTop: '1px solid var(--border)',
                }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                        Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPage * itemsPerPage, totalFiltered)}</strong> of <strong>{totalFiltered}</strong> plans
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ← Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => goToPage(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    // ==================== RENDER ====================
    return (
        <>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
                        <CreditCard size={20} style={{ verticalAlign: '-4px' }} /> Pricing Plans
                    </h2>
                    <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
                        Choose the plan that best fits your organization's needs.
                    </p>
                </div>
            </div>

            {/* ===== STATS GRID ===== */}
            <div className="stats-grid" style={{ marginBottom: '20px' }}>
                <div className="stat-card gold">
                    <div className="stat-label">Total Plans</div>
                    <div className="stat-value">{loading && !plans.length ? '...' : totalPlans}</div>
                    <div className="stat-change up">↑ available</div>
                    <div className="stat-icon"><CreditCard size={20} /></div>
                </div>

                <div className="stat-card green">
                    <div className="stat-label">Current Plan</div>
                    <div className="stat-value">{loading && !selectedPlan ? '...' : currentPlanName}</div>
                    <div className="stat-change up">↑ active</div>
                    <div className="stat-icon"><CheckCircle2 size={20} /></div>
                </div>

                {mostPopular ? (
                    <div className="stat-card blue">
                        <div className="stat-label">Most Popular</div>
                        <div className="stat-value">{mostPopular.name}</div>
                        <div className="stat-change up">⭐ recommended</div>
                        <div className="stat-icon"><Crown size={20} /></div>
                    </div>
                ) : (
                    <div className="stat-card blue">
                        <div className="stat-label">Most Popular</div>
                        <div className="stat-value">{loading ? '...' : '—'}</div>
                        <div className="stat-change up">⭐ recommended</div>
                        <div className="stat-icon"><Crown size={20} /></div>
                    </div>
                )}

                <div className="stat-card purple">
                    <div className="stat-label">Custom Plans</div>
                    <div className="stat-value">{loading && !plans.length ? '...' : customPlans}</div>
                    <div className="stat-change up">↑ tailored</div>
                    <div className="stat-icon"><Star size={20} /></div>
                </div>
            </div>

            {/* ===== FILTER BAR ===== */}
            <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
                        <span className="search-icon"><Search size={14} /></span>
                        <input
                            type="text"
                            placeholder="Search plans..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading && !plans.length}
                        />
                    </div>
                    <select
                        className="form-select"
                        style={{ width: '140px' }}
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        disabled={loading && !plans.length}
                    >
                        <option value="">All Plans</option>
                        <option value="standard">Standard</option>
                        <option value="custom">Custom</option>
                    </select>
                    <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                        <button
                            className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                            onClick={() => setViewMode('grid')}
                            disabled={loading && !plans.length}
                        >
                            <LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid
                        </button>
                        <button
                            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                            onClick={() => setViewMode('list')}
                            disabled={loading && !plans.length}
                        >
                            <List size={14} style={{ verticalAlign: '-2px' }} /> List
                        </button>
                    </div>
                </div>
            </div>

            {/* ===== PLANS DISPLAY ===== */}
            {loading && !plans.length ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Loader2 size={36} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
                    <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading plans...</div>
                </div>
            ) : filteredPlans.length > 0 ? (
                viewMode === 'grid' ? renderGridView() : renderListView()
            ) : (
                <div className="empty-state">
                    <div className="empty-icon"><CreditCard size={40} strokeWidth={1.5} /></div>
                    <div className="empty-title">No plans match your filters</div>
                    <div className="empty-text">Try adjusting your search or filter criteria.</div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 16px;
                }
                .stat-card {
                    position: relative;
                    padding: 16px 20px;
                    background: var(--white);
                    border: 1px solid var(--border);
                    border-radius: var(--radius);
                    box-shadow: var(--shadow);
                    transition: transform 0.2s, box-shadow 0.2s;
                    cursor: default;
                }
                .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-lg);
                }
                .stat-card .stat-label {
                    font-size: 12px;
                    color: var(--text-light);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    font-weight: 600;
                }
                .stat-card .stat-value {
                    font-size: 28px;
                    font-weight: 700;
                    font-family: 'Playfair Display', serif;
                    color: var(--navy);
                    margin: 4px 0 2px;
                }
                .stat-card .stat-change {
                    font-size: 12px;
                    font-weight: 600;
                }
                .stat-card .stat-change.up {
                    color: var(--green);
                }
                .stat-card .stat-change.down {
                    color: var(--red);
                }
                .stat-card .stat-icon {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    opacity: 0.2;
                    color: var(--navy);
                }
                .stat-card.gold .stat-icon { color: var(--gold); }
                .stat-card.green .stat-icon { color: var(--green); }
                .stat-card.blue .stat-icon { color: var(--blue); }
                .stat-card.purple .stat-icon { color: var(--purple); }

                .badge {
                    padding: 3px 10px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 500;
                    display: inline-block;
                }
                .badge-gold {
                    background: var(--gold);
                    color: var(--navy);
                }
                .badge-blue {
                    background: var(--blue);
                    color: white;
                }
                .badge-navy {
                    background: var(--navy);
                    color: var(--gold-light);
                }

                .btn-gold {
                    background: var(--gold) !important;
                    color: var(--navy) !important;
                    border: none !important;
                }
                .btn-gold:hover {
                    background: var(--gold-dark) !important;
                    color: var(--navy) !important;
                }
                .btn-primary {
                    background: var(--gold) !important;
                    color: var(--navy) !important;
                    border: none !important;
                }
                .btn-primary:hover {
                    background: var(--gold-dark) !important;
                    color: var(--navy) !important;
                }
                .btn-ghost {
                    background: transparent !important;
                    color: var(--text) !important;
                    border: 1px solid var(--border) !important;
                }
                .btn-ghost:hover {
                    background: var(--cream) !important;
                    color: var(--text) !important;
                }
                .btn-sm {
                    padding: 4px 10px !important;
                    font-size: 12px !important;
                }
                .btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .empty-state {
                    text-align: center;
                    padding: 40px 0;
                }
                .empty-icon {
                    font-size: 32px;
                    margin-bottom: 12px;
                    color: var(--text-light);
                }
                .empty-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--text);
                    margin-bottom: 4px;
                }
                .empty-text {
                    font-size: 14px;
                    color: var(--text-light);
                }

                .table-wrap {
                    overflow-x: auto;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                }
                th {
                    text-align: left;
                    padding: 10px 12px;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: var(--text-light);
                    border-bottom: 2px solid var(--border);
                    background: var(--cream);
                }
                td {
                    padding: 10px 12px;
                    border-bottom: 1px solid var(--border-light);
                    font-size: 13px;
                }
                tr:hover td {
                    background: var(--cream);
                }

                @media (max-width: 1024px) {
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                    }
                    .pricing-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                    }
                }
                @media (max-width: 768px) {
                    .stats-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .pricing-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .card-header {
                        flex-direction: column;
                        align-items: flex-start !important;
                        gap: 10px;
                    }
                }
            `}</style>
        </>
    );
};

export default Pricing;